import { useAuth } from '@/contexts/AuthContext';
import { DashboardLayout } from '@/layouts/DashboardLayout';
import { Shield, Users, Building2, Activity, AlertCircle, Database } from 'lucide-react';

export default function SuperAdminDashboard() {
  const { user } = useAuth();

  const platformStats = [
    { label: 'Total Organizations', value: '127', change: '+5 this month', icon: Building2, color: 'text-blue-600' },
    { label: 'Total Users', value: '1,248', change: '+89 this month', icon: Users, color: 'text-green-600' },
    { label: 'Active Jobs', value: '456', change: '+34 this week', icon: Activity, color: 'text-purple-600' },
    { label: 'System Health', value: '99.9%', change: 'All systems operational', icon: Shield, color: 'text-emerald-600' },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-3">
              <Shield className="w-8 h-8 text-red-600" />
              <h1 className="text-3xl font-bold text-gray-900">
                Platform Administration
              </h1>
            </div>
            <p className="mt-1 text-sm text-gray-600">
              System-wide monitoring and management • {user?.email}
            </p>
          </div>
        </div>

        {/* Platform Stats */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {platformStats.map((stat, index) => (
            <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className={`p-2 rounded-lg bg-gray-50 ${stat.color}`}>
                  <stat.icon className="w-6 h-6" />
                </div>
              </div>
              <p className="text-sm font-medium text-gray-600">{stat.label}</p>
              <p className="mt-2 text-3xl font-bold text-gray-900">{stat.value}</p>
              <p className="mt-2 text-xs text-gray-500">{stat.change}</p>
            </div>
          ))}
        </div>

        {/* Admin Tools */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Administration Tools</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button className="flex items-center p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition-colors">
              <Users className="w-8 h-8 text-red-600 mr-3" />
              <div className="text-left">
                <p className="font-medium text-gray-900">User Management</p>
                <p className="text-sm text-gray-500">Manage all platform users</p>
              </div>
            </button>

            <button className="flex items-center p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition-colors">
              <Building2 className="w-8 h-8 text-red-600 mr-3" />
              <div className="text-left">
                <p className="font-medium text-gray-900">Organizations</p>
                <p className="text-sm text-gray-500">View all organizations</p>
              </div>
            </button>

            <button className="flex items-center p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition-colors">
              <Database className="w-8 h-8 text-red-600 mr-3" />
              <div className="text-left">
                <p className="font-medium text-gray-900">System Logs</p>
                <p className="text-sm text-gray-500">View system activity</p>
              </div>
            </button>
          </div>
        </div>

        {/* System Alerts */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">System Alerts</h2>
          <div className="space-y-3">
            <div className="flex items-start p-4 bg-green-50 border border-green-200 rounded-lg">
              <Activity className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-green-900">All systems operational</p>
                <p className="text-xs text-green-700 mt-1">Last checked: 2 minutes ago</p>
              </div>
            </div>

            <div className="flex items-start p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-yellow-900">Scheduled maintenance</p>
                <p className="text-xs text-yellow-700 mt-1">Database optimization scheduled for Sunday 2:00 AM UTC</p>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Platform Activity */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Platform Activity</h2>
          <div className="space-y-4">
            {[
              { action: 'New organization registered', detail: 'Acme Corp', time: '15 minutes ago' },
              { action: 'User reported content', detail: 'Job post #4567', time: '1 hour ago' },
              { action: 'Payment processed', detail: 'TechStart Inc - $299', time: '3 hours ago' },
              { action: 'System backup completed', detail: 'Full backup', time: '6 hours ago' },
            ].map((activity, index) => (
              <div key={index} className="flex items-start pb-4 border-b border-gray-100 last:border-0">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{activity.action}</p>
                  <p className="text-sm text-gray-600">{activity.detail}</p>
                </div>
                <span className="text-xs text-gray-500">{activity.time}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Admin Badge */}
        <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-lg border border-red-200 p-6">
          <div className="flex items-start">
            <Shield className="w-8 h-8 text-red-600 mr-4 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900">Super Administrator Access</h3>
              <p className="text-sm text-gray-600 mt-1">
                You have full platform access. Exercise caution when making system-wide changes.
              </p>
              <p className="text-xs text-gray-500 mt-2">
                User ID: {user?.id} • Email: {user?.email}
              </p>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
